import React from 'react';

import { permissionBindingsParam, usePermissions } from '../../authz/providers/PermissionsProvider/PermissionsProvider';
import {
  currentUserWorkspaceActions,
  PermissionsResolver,
} from '../../components/PermissionsResolver/PermissionsResolver';

export enum MLOpsUserActionType {
  // Project actions
  MLOPS_PROJECTS_LIST_EVENTS = 'actions/mlops/projects/LIST_EVENTS',

  // Artifacts actions
  MLOPS_ARTIFACTS_GET = 'actions/mlops/artifacts/GET',
  MLOPS_ARTIFACTS_LIST_ENTITY = 'actions/mlops/artifacts/LIST_ENTITY',
  MLOPS_ARTIFACTS_CREATE = 'actions/mlops/artifacts/CREATE',
  MLOPS_ARTIFACTS_UPLOAD = 'actions/mlops/artifacts/UPLOAD',
  MLOPS_ARTIFACTS_DOWNLOAD = 'actions/mlops/artifacts/DOWNLOAD',
  MLOPS_ARTIFACTS_UPDATE = 'actions/mlops/artifacts/UPDATE',
  MLOPS_ARTIFACTS_DELETE = 'actions/mlops/artifacts/DELETE',

  // Experiment actions
  MLOPS_EXPERIMENTS_GET = 'actions/mlops/experiments/GET',
  MLOPS_EXPERIMENTS_LIST = 'actions/mlops/experiments/LIST',
  MLOPS_EXPERIMENTS_COUNT = 'actions/mlops/experiments/COUNT',
  MLOPS_EXPERIMENTS_CREATE = 'actions/mlops/experiments/CREATE',
  MLOPS_EXPERIMENTS_UPDATE = 'actions/mlops/experiments/UPDATE',
  MLOPS_EXPERIMENTS_DELETE = 'actions/mlops/experiments/DELETE',
  MLOPS_EXPERIMENTS_RECOVER_DELETED = 'actions/mlops/experiments/RECOVER_DELETED',
  MLOPS_EXPERIMENTS_LINK_INTO_PROJECT = 'actions/mlops/experiments/LINK_INTO_PROJECT',
  MLOPS_EXPERIMENTS_UNLINK_FROM_PROJECT = 'actions/mlops/experiments/UNLINK_FROM_PROJECT',

  // Experiment metrics actions
  MLOPS_EXPERIMENTS_LIST_METRICS = 'actions/mlops/experiments/LIST_METRICS',
  MLOPS_EXPERIMENTS_CREATE_METRIC = 'actions/mlops/experiments/CREATE_METRIC',

  // Experiment tags actions
  MLOPS_EXPERIMENTS_TAG = 'actions/mlops/experiments/TAG',
  MLOPS_EXPERIMENTS_UNTAG = 'actions/mlops/experiments/UNTAG',

  // Experiment comments actions
  MLOPS_EXPERIMENTS_LIST_COMMENTS = 'actions/mlops/experiments/LIST_COMMENTS',
  MLOPS_EXPERIMENTS_CREATE_COMMENT = 'actions/mlops/experiments/CREATE_COMMENT',

  // Model Registry actions
  MLOPS_MODEL_REGISTRY_GET = 'actions/mlops/modelRegistry/GET',
  MLOPS_MODEL_REGISTRY_LIST = 'actions/mlops/modelRegistry/LIST',
  MLOPS_MODEL_REGISTRY_LIST_ALL = 'actions/mlops/modelRegistry/LIST_ALL', // this is a global permission, use `//mlops` resource with it
  MLOPS_MODEL_REGISTRY_COUNT = 'actions/mlops/modelRegistry/COUNT',
  MLOPS_MODEL_REGISTRY_CREATE = 'actions/mlops/modelRegistry/CREATE',
  MLOPS_MODEL_REGISTRY_UPDATE = 'actions/mlops/modelRegistry/UPDATE',
  MLOPS_MODEL_REGISTRY_DELETE = 'actions/mlops/modelRegistry/DELETE',
  MLOPS_MODEL_REGISTRY_GET_VERSION = 'actions/mlops/modelRegistry/GET_VERSION',
  MLOPS_MODEL_REGISTRY_LIST_VERSION = 'actions/mlops/modelRegistry/LIST_VERSION',
  MLOPS_MODEL_REGISTRY_COUNT_VERSION = 'actions/mlops/modelRegistry/COUNT_VERSION',
  MLOPS_MODEL_REGISTRY_CREATE_VERSION = 'actions/mlops/modelRegistry/CREATE_VERSION',
  MLOPS_MODEL_REGISTRY_DELETE_VERSION = 'actions/mlops/modelRegistry/DELETE_VERSION',
  MLOPS_MODEL_REGISTRY_INACTIVATE = 'actions/mlops/modelRegistry/INACTIVATE',

  // Deployment Environments actions
  MLOPS_DEPLOYMENT_ENVIRONMENTS_GET = 'actions/mlops/deploymentEnvironments/GET',
  MLOPS_DEPLOYMENT_ENVIRONMENTS_LIST = 'actions/mlops/deploymentEnvironments/LIST',
  MLOPS_DEPLOYMENT_ENVIRONMENTS_CREATE = 'actions/mlops/deploymentEnvironments/CREATE',
  MLOPS_DEPLOYMENT_ENVIRONMENTS_UPDATE = 'actions/mlops/deploymentEnvironments/UPDATE',
  MLOPS_DEPLOYMENT_ENVIRONMENTS_DELETE = 'actions/mlops/deploymentEnvironments/DELETE',

  // Deployment actions
  MLOPS_DEPLOYMENTS_GET = 'actions/mlops/deployments/GET',
  MLOPS_DEPLOYMENTS_LIST = 'actions/mlops/deployments/LIST',
  MLOPS_DEPLOYMENTS_LIST_ALL = 'actions/mlops/deployments/LIST_ALL', // this is a global permission, use `//mlops` resource with it
  MLOPS_DEPLOYMENTS_CREATE = 'actions/mlops/deployments/CREATE',
  MLOPS_DEPLOYMENTS_UPDATE = 'actions/mlops/deployments/UPDATE',
  MLOPS_DEPLOYMENTS_DELETE = 'actions/mlops/deployments/DELETE',
  MLOPS_DEPLOYMENTS_RETRY = 'actions/mlops/deployments/RETRY',
  MLOPS_DEPLOYMENTS_PAUSE = 'actions/mlops/deployments/PAUSE',
  MLOPS_DEPLOYMENTS_RESUME = 'actions/mlops/deployments/RESUME',

  // Batch Scoring Jobs actions
  MLOPS_BATCH_SCORING_JOBS_GET = 'actions/mlops/batchScoringJobs/GET',
  MLOPS_BATCH_SCORING_JOBS_LIST = 'actions/mlops/batchScoringJobs/LIST',
  MLOPS_BATCH_SCORING_JOBS_CREATE = 'actions/mlops/batchScoringJobs/CREATE',
  MLOPS_BATCH_SCORING_JOBS_DELETE = 'actions/mlops/batchScoringJobs/DELETE',
  MLOPS_BATCH_SCORING_JOBS_CANCEL = 'actions/mlops/batchScoringJobs/CANCEL',

  // Endpoints actions
  MLOPS_ENDPOINTS_GET = 'actions/mlops/endpoints/GET',
  MLOPS_ENDPOINTS_LIST = 'actions/mlops/endpoints/LIST',
  MLOPS_ENDPOINTS_CREATE = 'actions/mlops/endpoints/CREATE',
  MLOPS_ENDPOINTS_UPDATE = 'actions/mlops/endpoints/UPDATE',
  MLOPS_ENDPOINTS_DELETE = 'actions/mlops/endpoints/DELETE',

  // Tags actions
  MLOPS_TAGS_GET = 'actions/mlops/tags/GET',
  MLOPS_TAGS_LIST = 'actions/mlops/tags/LIST',
  MLOPS_TAGS_CREATE = 'actions/mlops/tags/CREATE',
  MLOPS_TAGS_UPDATE = 'actions/mlops/tags/UPDATE',
  MLOPS_TAGS_DELETE = 'actions/mlops/tags/DELETE',

  // Comments actions
  MLOPS_COMMENTS_GET = 'actions/mlops/comments/GET',

  // Datasets actions
  MLOPS_DATASETS_GET = 'actions/mlops/datasets/GET',
  MLOPS_DATASETS_LIST = 'actions/mlops/datasets/LIST',
  MLOPS_DATASETS_CREATE = 'actions/mlops/datasets/CREATE',
  MLOPS_DATASETS_UPDATE = 'actions/mlops/datasets/UPDATE',
  MLOPS_DATASETS_DELETE = 'actions/mlops/datasets/DELETE',
  MLOPS_DATASETS_TAG = 'actions/mlops/datasets/TAG',
  MLOPS_DATASETS_UNTAG = 'actions/mlops/datasets/UNTAG',
  MLOPS_DATASETS_LINK_INTO_PROJECT = 'actions/mlops/datasets/LINK_INTO_PROJECT',
  MLOPS_DATASETS_UNLINK_FROM_PROJECT = 'actions/mlops/datasets/UNLINK_FROM_PROJECT',

  /*
  * These are present among the MLOps role actions, but not used within the MLOps UI.

  WORKSPACESERVER_WORKSPACES_GET = 'actions/workspaceserver/workspaces/GET',
  WORKSPACESERVER_WORKSPACES_UPDATE = 'actions/workspaceserver/workspaces/UPDATE',
  WORKSPACESERVER_WORKSPACES_DELETE = 'actions/workspaceserver/workspaces/DELETE',
  WORKSPACESERVER_WORKSPACES_LIST = 'actions/workspaceserver/workspaces/LIST',

  POLICYSERVER_ROLE_BINDINGS_READ = 'actions/policyserver/roleBindings/READ',

  ALL_MLOPS_ACTIONS = 'actions/mlops/**'
  */
}

const MLOpsModelsPageUserActions = [
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_GET_VERSION,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_CREATE_VERSION,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_DELETE_VERSION,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_CREATE,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_DELETE,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_UPDATE,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_GET,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_LIST,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_LIST_VERSION,
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_COUNT_VERSION,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_DELETE,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_CREATE,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_GET,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_LIST,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_UPDATE,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_TAG,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_UNTAG,
  MLOpsUserActionType.MLOPS_TAGS_GET,
  MLOpsUserActionType.MLOPS_TAGS_CREATE,
  MLOpsUserActionType.MLOPS_TAGS_LIST,
  MLOpsUserActionType.MLOPS_TAGS_UPDATE,
  MLOpsUserActionType.MLOPS_TAGS_DELETE,
  MLOpsUserActionType.MLOPS_ARTIFACTS_CREATE,
  MLOpsUserActionType.MLOPS_ARTIFACTS_GET,
  MLOpsUserActionType.MLOPS_ARTIFACTS_LIST_ENTITY,
  MLOpsUserActionType.MLOPS_ARTIFACTS_DELETE,
  MLOpsUserActionType.MLOPS_ARTIFACTS_UPDATE,
  MLOpsUserActionType.MLOPS_ARTIFACTS_UPLOAD,
  MLOpsUserActionType.MLOPS_BATCH_SCORING_JOBS_GET,
  MLOpsUserActionType.MLOPS_BATCH_SCORING_JOBS_CREATE,
  MLOpsUserActionType.MLOPS_BATCH_SCORING_JOBS_LIST,
  MLOpsUserActionType.MLOPS_BATCH_SCORING_JOBS_DELETE,
  MLOpsUserActionType.MLOPS_BATCH_SCORING_JOBS_CANCEL,
  MLOpsUserActionType.MLOPS_DEPLOYMENTS_GET,
  MLOpsUserActionType.MLOPS_DEPLOYMENTS_CREATE,
  MLOpsUserActionType.MLOPS_DEPLOYMENTS_LIST,
  MLOpsUserActionType.MLOPS_DEPLOYMENTS_UPDATE,
  MLOpsUserActionType.MLOPS_DEPLOYMENTS_DELETE,
  MLOpsUserActionType.MLOPS_DEPLOYMENTS_RETRY,
  MLOpsUserActionType.MLOPS_DEPLOYMENTS_PAUSE,
  MLOpsUserActionType.MLOPS_DEPLOYMENTS_RESUME,
  MLOpsUserActionType.MLOPS_ENDPOINTS_GET,
  MLOpsUserActionType.MLOPS_ENDPOINTS_LIST,
  MLOpsUserActionType.MLOPS_ENDPOINTS_CREATE,
  MLOpsUserActionType.MLOPS_ENDPOINTS_UPDATE,
  MLOpsUserActionType.MLOPS_ENDPOINTS_DELETE,
  MLOpsUserActionType.MLOPS_PROJECTS_LIST_EVENTS,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_LIST_COMMENTS,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_CREATE_COMMENT,
];

export type GranularMLOpsPermissions = {
  canGetModelVersion: boolean;
  canCreateModelVersion: boolean;
  canDeleteModelVersion: boolean;
  canCreateModel: boolean;
  canDeleteModel: boolean;
  canUpdateModel: boolean;
  canGetModel: boolean;
  canListModels: boolean;
  canListModelVersions: boolean;
  canCountModelVersions: boolean;
  canDeleteExperiments: boolean;
  canCreateExperiments: boolean;
  canGetExperiments: boolean;
  canListExperiments: boolean;
  canUpdateExperiments: boolean;
  canTagExperiments: boolean;
  canUntagExperiments: boolean;
  canGetTags: boolean;
  canCreateTags: boolean;
  canListTags: boolean;
  canUpdateTags: boolean;
  canDeleteTags: boolean;
  canCreateArtifacts: boolean;
  canGetArtifacts: boolean;
  canListArtifacts: boolean;
  canDeleteArtifacts: boolean;
  canUpdateArtifacts: boolean;
  canUploadArtifacts: boolean;
  canGetBatchScoringJobs: boolean;
  canCreateBatchScoringJobs: boolean;
  canListBatchScoringJobs: boolean;
  canDeleteBatchScoringJobs: boolean;
  canCancelBatchScoringJobs: boolean;
  canGetDeployments: boolean;
  canCreateDeployments: boolean;
  canListDeployments: boolean;
  canUpdateDeployments: boolean;
  canDeleteDeployments: boolean;
  canRetryDeployments: boolean;
  canPauseDeployments: boolean;
  canResumeDeployments: boolean;
  canGetEndpoints: boolean;
  canListEndpoints: boolean;
  canCreateEndpoints: boolean;
  canUpdateEndpoints: boolean;
  canDeleteEndpoints: boolean;
  isAbleToCreateModel: boolean;
  isAbleToListModels: boolean;
  isAbleToViewModelTags: boolean;
  isAbleToChangeModelTags: boolean;
  isAbleToCreateModelVersion: boolean;
  isAbleToDeleteModelVersion: boolean;
  isAbleToDeleteModel: boolean;
  isAbleToViewModel: boolean;
  isAbleToUpdateModel: boolean;
  isAbleToViewDeployment: boolean;
  isAbleToViewBatchScoringJob: boolean;
  isAbleToViewDeploymentMonitoring: boolean;
  isAbleToSetDeploymentMonitoring: boolean;
  isAbleToViewDeployments: boolean;
  isAbleToViewDeploymentStatus: boolean;
  isAbleToCreateDeployments: boolean;
  isAbleToUpdateDeployments: boolean;
  isAbleToDeleteDeployments: boolean;
  isAbleToRetryDeployments: boolean;
  isAbleToPauseDeployments: boolean;
  isAbleToResumeDeployments: boolean;
  isAbleToCreateEndpoints: boolean;
  isAbleToViewEndpoints: boolean;
  isAbleToDeleteEndpoints: boolean;
  isAbleToQuickScoreDeployment: boolean;
  isAbleToListBatchScoringJobs: boolean;
  isAbleToViewBatchScoringJobs: boolean;
  isAbleToCreateBatchScoringJobs: boolean;
  isAbleToDeleteBatchScoringJobs: boolean;
  isAbleToCancelBatchScoringJobs: boolean;
  isAbleToSetBatchScoringMonitoring: boolean;
  isAbleToViewMLOpsEventsDashboardWidget: boolean;
  isAbleToViewDeploymentsDashboardWidget: boolean;
  isAbleToViewScroingDashboardWidget: boolean;
  isAbleToListExperimentComments: boolean;
  isAbleToCreateExperimentComment: boolean;
  loading: boolean;
  hasAccessToDashboardPage: boolean;
  hasAccessToModelsPage: boolean;
  hasAccessToModelDetailPage_create: boolean;
  hasAccessToModelDetailPage_view: boolean;
  hasAccessToDeploymentsPage: boolean;
  hasAccessToDeploymentDetailPage_create: boolean;
  hasAccessToDeploymentDetailPage_view: boolean;
  hasAccessToDeploymentEndpointsTab: boolean;
  hasAccessToDeploymentQuickScoreTab: boolean;
  hasAccessToDeploymentMonitoringTab: boolean;
  hasAccessToBatchScoringPage: boolean;
  hasAccessToBatchScoringDetailPage_create: boolean;
  hasAccessToBatchScoringDetailPage_view: boolean;
  hasAccessToBatchScoringMonitoringTab: boolean;
  hasAccessToTagsPage: boolean;
};

const getGranularMLOpsPermissions = (allowedActions: boolean[]): GranularMLOpsPermissions => {
  // Each action represents one endpoint user is allowed to call with his current policies.
  const [
    canGetModelVersion,
    canCreateModelVersion,
    canDeleteModelVersion,
    canCreateModel,
    canDeleteModel,
    canUpdateModel,
    canGetModel,
    canListModels,
    canListModelVersions,
    canCountModelVersions,
    canDeleteExperiments,
    canCreateExperiments,
    canGetExperiments,
    canListExperiments,
    canUpdateExperiments,
    canTagExperiments,
    canUntagExperiments,
    canGetTags,
    canCreateTags,
    canListTags,
    canUpdateTags,
    canDeleteTags,
    canCreateArtifacts,
    canGetArtifacts,
    canListArtifacts,
    canDeleteArtifacts,
    canUpdateArtifacts,
    canUploadArtifacts,
    canGetBatchScoringJobs,
    canCreateBatchScoringJobs,
    canListBatchScoringJobs,
    canDeleteBatchScoringJobs,
    canCancelBatchScoringJobs,
    canGetDeployments,
    canCreateDeployments,
    canListDeployments,
    canUpdateDeployments,
    canDeleteDeployments,
    canRetryDeployments,
    canPauseDeployments,
    canResumeDeployments,
    canGetEndpoints,
    canListEndpoints,
    canCreateEndpoints,
    canUpdateEndpoints,
    canDeleteEndpoints,
    canListProjectEvents,
    canListExperimentComments,
    canCreateExperimentComment,
    loading,
  ] = allowedActions;

  // "isAbleTo" variables are used to determine if user has all necessary policies to perform certain actions in the UI.
  const isAbleToCreateModel =
      canCreateModelVersion &&
      canCreateExperiments &&
      canCreateArtifacts &&
      canUploadArtifacts &&
      canUpdateArtifacts &&
      canUpdateModel,
    isAbleToListModels = canListModels && canListModelVersions && canGetExperiments,
    isAbleToViewModelTags = canGetModelVersion && canGetExperiments && canListTags,
    isAbleToChangeModelTags = isAbleToViewModelTags && (canTagExperiments || canUntagExperiments),
    isAbleToCreateModelVersion = canCreateModelVersion && isAbleToCreateModel,
    isAbleToDeleteModelVersion = canDeleteModelVersion && canDeleteExperiments,
    isAbleToDeleteModel = isAbleToDeleteModelVersion && canDeleteModel,
    isAbleToViewModel = canGetModel && canListModelVersions && canGetExperiments && canGetModelVersion,
    // TODO: Add other policies, like canGetModel, etc.
    isAbleToUpdateModel = canUpdateModel,
    // If can't list endpoints, show without it. && canListEndpoints,
    // TODO: Check if these are without policies - batchGetDeploymentStatus, batchGetDeploymentMetadata, GetDeploymentStatus
    isAbleToViewDeployments = canListDeployments,
    isAbleToViewDeploymentStatus = canGetDeployments || canListDeployments,
    isAbleToViewDeployment = canGetDeployments && canListModels && canListModelVersions && canGetExperiments,
    isAbleToCreateDeployments = canListModels && canListModelVersions && canCreateDeployments && canGetExperiments,
    isAbleToViewDeploymentMonitoring = isAbleToViewDeployment,
    // TODO: Check if this is correct.
    isAbleToSetDeploymentMonitoring = isAbleToCreateDeployments && canGetExperiments,
    isAbleToUpdateDeployments = isAbleToViewDeployment && canUpdateDeployments,
    isAbleToDeleteDeployments = canDeleteDeployments && (canGetDeployments || canListDeployments),
    isAbleToRetryDeployments = canRetryDeployments && (canGetDeployments || canListDeployments),
    isAbleToPauseDeployments = canPauseDeployments && (canGetDeployments || canListDeployments),
    isAbleToResumeDeployments = canResumeDeployments && (canGetDeployments || canListDeployments),
    // TODO: Check if this is correct.
    isAbleToCreateEndpoints = canGetDeployments && canCreateEndpoints,
    isAbleToViewEndpoints = canGetDeployments, // For accessing scoring endpoint only GetDeploymentMetadata is enoguh, so there is no need to canListEndpoints.
    isAbleToDeleteEndpoints = isAbleToViewEndpoints && canDeleteEndpoints,
    isAbleToQuickScoreDeployment = canGetDeployments,
    isAbleToListBatchScoringJobs = canListBatchScoringJobs,
    // TODO: Make ModelSelector dependencies unnecessary and hide this component if user can't list models or model versions.
    isAbleToViewBatchScoringJobs =
      canGetBatchScoringJobs && canGetModelVersion && canListModels && canListModelVersions,
    isAbleToCreateBatchScoringJobs =
      canCreateBatchScoringJobs && canGetModelVersion && canListModels && canListModelVersions,
    isAbleToDeleteBatchScoringJobs = canDeleteBatchScoringJobs && (canGetBatchScoringJobs || canListBatchScoringJobs),
    isAbleToCancelBatchScoringJobs = canCancelBatchScoringJobs && (canGetBatchScoringJobs || canListBatchScoringJobs),
    isAbleToSetBatchScoringMonitoring = isAbleToCreateBatchScoringJobs && canGetExperiments,
    isAbleToViewBatchScoringJob = canGetBatchScoringJobs && canGetModelVersion && canListModels,
    // TODO: Add permission based handlers for event tooltip details calls.
    isAbleToViewMLOpsEventsDashboardWidget = canListProjectEvents,
    isAbleToViewDeploymentsDashboardWidget = isAbleToViewDeployments,
    isAbleToViewScroingDashboardWidget = isAbleToListBatchScoringJobs,
    isAbleToListExperimentComments = canListExperimentComments,
    isAbleToCreateExperimentComment = canCreateExperimentComment;

  // "hasAccessTo" variables are used to determine if user has access to certain pages in the UI.
  const hasAccessToDashboardPage =
      isAbleToViewMLOpsEventsDashboardWidget ||
      isAbleToViewDeploymentsDashboardWidget ||
      isAbleToViewScroingDashboardWidget,
    hasAccessToModelsPage = isAbleToListModels || isAbleToCreateModel,
    hasAccessToModelDetailPage_create = isAbleToCreateModel,
    hasAccessToModelDetailPage_view = isAbleToViewModel || isAbleToUpdateModel,
    hasAccessToDeploymentsPage = isAbleToViewDeployments || isAbleToCreateDeployments,
    hasAccessToDeploymentDetailPage_create = isAbleToCreateDeployments,
    hasAccessToDeploymentDetailPage_view = isAbleToViewDeployment,
    hasAccessToDeploymentEndpointsTab = isAbleToCreateEndpoints || isAbleToViewEndpoints,
    hasAccessToDeploymentQuickScoreTab = isAbleToQuickScoreDeployment,
    hasAccessToDeploymentMonitoringTab = isAbleToViewDeployment,
    hasAccessToBatchScoringPage = isAbleToListBatchScoringJobs || isAbleToCreateBatchScoringJobs,
    hasAccessToBatchScoringDetailPage_create = isAbleToCreateBatchScoringJobs,
    hasAccessToBatchScoringDetailPage_view = isAbleToViewBatchScoringJobs,
    hasAccessToBatchScoringMonitoringTab = isAbleToViewBatchScoringJob,
    hasAccessToTagsPage =
      canListTags || canCreateTags || (canListTags && canUpdateTags) || (canListTags && canDeleteTags);

  return {
    canGetModelVersion,
    canCreateModelVersion,
    canDeleteModelVersion,
    canCreateModel,
    canDeleteModel,
    canUpdateModel,
    canGetModel,
    canListModels,
    canListModelVersions,
    canCountModelVersions,
    canDeleteExperiments,
    canCreateExperiments,
    canGetExperiments,
    canListExperiments,
    canUpdateExperiments,
    canTagExperiments,
    canUntagExperiments,
    canGetTags,
    canCreateTags,
    canListTags,
    canUpdateTags,
    canDeleteTags,
    canCreateArtifacts,
    canGetArtifacts,
    canListArtifacts,
    canDeleteArtifacts,
    canUpdateArtifacts,
    canUploadArtifacts,
    canGetBatchScoringJobs,
    canCreateBatchScoringJobs,
    canListBatchScoringJobs,
    canDeleteBatchScoringJobs,
    canCancelBatchScoringJobs,
    canGetDeployments,
    canCreateDeployments,
    canListDeployments,
    canUpdateDeployments,
    canDeleteDeployments,
    canRetryDeployments,
    canPauseDeployments,
    canResumeDeployments,
    canGetEndpoints,
    canListEndpoints,
    canCreateEndpoints,
    canUpdateEndpoints,
    canDeleteEndpoints,
    isAbleToCreateModel,
    isAbleToListModels,
    isAbleToViewModelTags,
    isAbleToChangeModelTags,
    isAbleToCreateModelVersion,
    isAbleToDeleteModelVersion,
    isAbleToDeleteModel,
    isAbleToViewModel,
    isAbleToUpdateModel,
    isAbleToViewDeployment,
    isAbleToViewBatchScoringJob,
    isAbleToViewDeploymentMonitoring,
    isAbleToSetDeploymentMonitoring,
    isAbleToViewDeployments,
    isAbleToViewDeploymentStatus,
    isAbleToCreateDeployments,
    isAbleToUpdateDeployments,
    isAbleToDeleteDeployments,
    isAbleToRetryDeployments,
    isAbleToPauseDeployments,
    isAbleToResumeDeployments,
    isAbleToCreateEndpoints,
    isAbleToViewEndpoints,
    isAbleToDeleteEndpoints,
    isAbleToQuickScoreDeployment,
    isAbleToListBatchScoringJobs,
    isAbleToViewBatchScoringJobs,
    isAbleToCreateBatchScoringJobs,
    isAbleToDeleteBatchScoringJobs,
    isAbleToCancelBatchScoringJobs,
    isAbleToSetBatchScoringMonitoring,
    isAbleToViewMLOpsEventsDashboardWidget,
    isAbleToViewDeploymentsDashboardWidget,
    isAbleToViewScroingDashboardWidget,
    isAbleToListExperimentComments,
    isAbleToCreateExperimentComment,
    loading,
    hasAccessToDashboardPage,
    hasAccessToModelsPage,
    hasAccessToModelDetailPage_create,
    hasAccessToModelDetailPage_view,
    hasAccessToDeploymentsPage,
    hasAccessToDeploymentDetailPage_create,
    hasAccessToDeploymentDetailPage_view,
    hasAccessToDeploymentEndpointsTab,
    hasAccessToDeploymentQuickScoreTab,
    hasAccessToDeploymentMonitoringTab,
    hasAccessToBatchScoringPage,
    hasAccessToBatchScoringDetailPage_create,
    hasAccessToBatchScoringDetailPage_view,
    hasAccessToBatchScoringMonitoringTab,
    hasAccessToTagsPage,
  };
};

export const useGranularMLOpsPermissions = (workspaceFQDN?: string): GranularMLOpsPermissions => {
  const allowedActions = usePermissions(MLOpsModelsPageUserActions, `${permissionBindingsParam}${workspaceFQDN}`);
  const permissionsLoading = allowedActions.at(-1);
  return React.useMemo(() => getGranularMLOpsPermissions(allowedActions), [workspaceFQDN, permissionsLoading]);
};

/**
 * A list of user actions needed to view model tags.
 */
export const isAbleToViewModelTags: MLOpsUserActionType[] = [
  MLOpsUserActionType.MLOPS_MODEL_REGISTRY_GET_VERSION,
  MLOpsUserActionType.MLOPS_EXPERIMENTS_GET,
  MLOpsUserActionType.MLOPS_TAGS_LIST,
];

export type GranularMLOpsPermissionsResolverProps = {
  children: (permissions: GranularMLOpsPermissions) => React.ReactNode;
  workspaceFQDN?: string;
};

/**
 * A render-prop component for resolving granular MLOps permissions useful when you cannot use hooks.
 */
export const GranularMLOpsPermissionsResolver = (props: GranularMLOpsPermissionsResolverProps) => {
  return (
    <PermissionsResolver
      actions={
        props.workspaceFQDN
          ? currentUserWorkspaceActions(props.workspaceFQDN, MLOpsModelsPageUserActions)
          : MLOpsModelsPageUserActions
      }
    >
      {({ results }) => {
        return props.children(getGranularMLOpsPermissions(results as boolean[]));
      }}
    </PermissionsResolver>
  );
};

export const resourceMLOps = '//mlops';
